import { FC, ReactNode, useState } from "react";

import Button from "../Button";
import Input from "../Input";
import MonthPicker from "../MonthPicker";
import { Select as OldSelect } from "../OldSelect";

import DatePicker from "../DatePicker";
import Loader from "../Loader";
import Modal from "../Modal";
import ModalHeader from "../Modal/ModalHeader";
import Select from "../Select";
import Text from "../Text";
import TextArea from "../TextArea";
import styles from "./styles.module.scss";

export type FieldType =
    | "text"
    | "date"
    | "oldDate"
    | "select"
    | "select-multiple"
    | "textarea"
    | "month"
    | "radio"
    | "file";

type FieldBase<T extends FieldType> = {
    id: string;
    label: string | ReactNode;
    type: T;
    options?: { value: string; label: string }[];
    onBlur?: () => void;
    min?: string | number | Date;
    max?: string | number | Date;
    error?: string;
};

export type Field<T extends FieldType> = T extends "date"
    ? FieldBase<T> & {
          value?: Date;
          onChange?: (value: Date) => void;
          min?: Date;
          max?: Date;
      }
    : T extends "select-multiple"
    ? FieldBase<T> & {
          value?: string[];
          onChange?: (value: string[]) => void;
      }
    : T extends "file"
    ? FieldBase<T> & {
          value?: File;
          onChange?: (value: File | undefined) => void;
          /**
           * Filtre explorateur + validation. Défaut images
           * (`.png,.jpg,.jpeg`) — passer `.csv,text/csv` pour un import CSV.
           */
          accept?: string;
      }
    : FieldBase<T> & {
          value?: string;
          onChange?: (value: string) => void;
      };

type ModalFormProps<T extends FieldType> = {
    open: boolean;
    handleClose: () => void;
    fields: Field<T>[];
    submitLabel?: string;
    onSubmit: () => void;
    title?: string;
    size?: "small" | "medium" | "large";
    className?: string;
    contentClassName?: string;
    /** Contenu affiché au-dessus des champs du formulaire */
    contentAbove?: React.ReactNode;
    customContent?: React.ReactNode;
    isLoading?: boolean;
};

const ModalForm: FC<ModalFormProps<FieldType>> = ({
    open,
    handleClose,
    fields,
    submitLabel = "Confirmer",
    onSubmit,
    title,
    size = "medium",
    className,
    contentClassName,
    contentAbove,
    customContent,
    isLoading: externalIsLoading = false,
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            await onSubmit();
        } catch (error) {
            console.error("Error submitting form:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            size={size}
            className={`${styles.modal} ${className || ""}`}
            open={open}
            onClose={handleClose}
        >
            <ModalHeader title={title || ""} handleClose={handleClose} />
            {!externalIsLoading && open ? (
                <>
                    <div className={`${styles.content} ${contentClassName}`}>
                        {contentAbove}
                        {fields.map((field) => (
                            <div
                                className={
                                    field.type === "textarea"
                                        ? styles.textarea_line
                                        : styles.input_line
                                }
                                key={field.id}
                            >
                                {typeof field.label === "string" ? (
                                    <Text
                                        variant="body-bold"
                                        className={styles.label}
                                    >
                                        {field.label}
                                    </Text>
                                ) : (
                                    field.label
                                )}
                                {field.type === "text" && (
                                    <Input
                                        className={styles.input}
                                        value={field.value as string}
                                        onChanged={field.onChange}
                                        onBlur={field.onBlur}
                                        error={field.error}
                                    />
                                )}
                                {field.type === "oldDate" && (
                                    <Input
                                        className={styles.input}
                                        type="date"
                                        value={field.value as string}
                                        onChanged={field.onChange}
                                        onBlur={field.onBlur}
                                        min={field.min as string}
                                        max={field.max as string}
                                        error={field.error}
                                    />
                                )}
                                {field.type === "date" && (
                                    <div className={styles.datePickerContainer}>
                                        <DatePicker
                                            mode="single"
                                            placement="bottom-end"
                                            date={field.value as Date}
                                            setDate={field.onChange}
                                            min={field.min as Date}
                                            max={field.max as Date}
                                            onBlur={field.onBlur}
                                            disableWeekends
                                            error={field.error}
                                        />
                                    </div>
                                )}
                                {field.type === "textarea" && (
                                    <TextArea
                                        maxLength={field.max as number}
                                        className={styles.textarea}
                                        value={field.value as string}
                                        onChange={(e) =>
                                            field.onChange?.(e.target.value)
                                        }
                                        error={field.error}
                                    />
                                )}
                                {field.type === "select" && (
                                    <OldSelect
                                        value={field.value as string}
                                        onChanged={
                                            field.onChange as
                                                | ((value: unknown) => void)
                                                | undefined
                                        }
                                        className={styles.select}
                                        error={field.error}
                                    >
                                        {field.options?.map((option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </OldSelect>
                                )}
                                {field.type === "select-multiple" && (
                                    <Select
                                        options={field.options || []}
                                        type="multiple"
                                        value={
                                            Array.isArray(field.value)
                                                ? field.value
                                                : []
                                        }
                                        onChange={(selected) =>
                                            field.onChange?.(
                                                selected as string[]
                                            )
                                        }
                                        classNameTrigger={styles.select}
                                        error={field.error}
                                    />
                                )}
                                {field.type === "month" && (
                                    <MonthPicker
                                        className={styles.monthPicker}
                                        value={field.value as string}
                                        onChange={field.onChange}
                                        error={field.error}
                                    />
                                )}
                                {field.type === "file" && (
                                    <Input
                                        id={field.id}
                                        fileInputId={`modal_file_${field.id}`}
                                        className={styles.input}
                                        type="file"
                                        accept={field.accept}
                                        value={
                                            field.value
                                                ? "Changer le fichier"
                                                : ""
                                        }
                                        onChanged={(files: FileList) => {
                                            const file = files[0] || undefined;
                                            if (!file) return;
                                            field.onChange?.(file);
                                        }}
                                        error={field.error}
                                    />
                                )}
                            </div>
                        ))}
                        {customContent && customContent}
                    </div>
                    <div className={styles.footer}>
                        <Button
                            className={styles.footer_button}
                            size="medium"
                            label={submitLabel}
                            onClick={handleSubmit}
                            isLoading={isLoading}
                        />
                    </div>
                </>
            ) : (
                <Loader />
            )}
        </Modal>
    );
};

export default ModalForm;
